import React, { useState } from "react";
import PoweredBy from "../components/PoweredBy";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useAppSettings, resolveImageUrl, useClientBrand } from "../context/AppSettingsContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Mail, ArrowRight, Eye, EyeOff, ChevronLeft } from "lucide-react";

// Accesso operatore: stessa grafica bianca/blu dell'area cliente (ClientLogin)
// Campi obbligatori: bordo più marcato e fondino celeste 5% finché vuoti
const reqCls = (v) => `h-12 border-2 border-zinc-300 text-base ${v ? "bg-white" : "bg-sky-500/5"}`;

export default function Login() {
  const { brand, brandDark: labelColor } = useClientBrand();
  const { login } = useAuth();
  const { settings } = useAppSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const logo = resolveImageUrl(settings.logo_url);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      nav(loc.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900 light-auth" data-testid="admin-login">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-zinc-100">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/cliente" className="h-10 w-10 rounded-2xl ring-1 ring-zinc-100 flex items-center justify-center hover:bg-blue-50" style={{ color: brand }} data-testid="login-back">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          {logo ? (
            <img src={logo} alt={settings.app_name} className="h-9 object-contain" />
          ) : (
            <span className="font-heading font-extrabold" style={{ color: brand }}>{settings.app_name}</span>
          )}
          <span className="h-10 w-10" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-8 space-y-6">
        <div className="text-center">
          <img src={logo || "/vivi-logo.png"} alt={settings.app_name} className="mx-auto h-24 w-auto object-contain mb-3 drop-shadow-sm" data-testid="login-vivi-logo" />
          <div className="font-heading font-extrabold text-lg tracking-tight mb-3 uppercase" style={{ color: brand }} data-testid="login-app-name">{settings.app_name || "Vivi Fidelity Club"}</div>
          <span className="inline-block text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-3" style={{ background: `${brand}12`, color: brand }} data-testid="login-badge">
            Accesso operatore
          </span>
          <h1 className="font-heading font-extrabold text-2xl">Bentornato al Circuito</h1>
          <p className="text-sm text-zinc-500 mt-1">Accedi con le credenziali del tuo staff.</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-sm font-semibold" style={{ color: labelColor }}>Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@dominio.it"
                className={`pl-9 ${reqCls(email)}`}
                autoComplete="username"
                required
                autoFocus
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw" className="text-sm font-semibold" style={{ color: labelColor }}>Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                id="pw"
                data-testid="login-password-input"
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="La tua password"
                className={`pl-9 pr-9 ${reqCls(password)}`}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                data-testid="login-password-toggle"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                aria-label={showPass ? "Nascondi password" : "Mostra password"}
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full h-12 text-white font-semibold text-base hover:brightness-95"
            style={{ background: brand }}
          >
            {loading ? "Attendere…" : "Entra nel Circuito"} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </form>

        <div className="rounded-2xl border border-zinc-100 bg-zinc-50 p-4 text-center text-sm text-zinc-500 space-y-1">
          <div>
            Sei un cliente?{" "}
            <Link to="/cliente/accedi" className="font-semibold hover:underline" style={{ color: brand }} data-testid="login-client-link">
              Accedi alla tua card
            </Link>
          </div>
          <div className="text-xs">
            Non hai ancora una card?{" "}
            <Link to="/registrati" className="font-semibold hover:underline" style={{ color: brand }} data-testid="login-register-link">
              Registrati
            </Link>
          </div>
        </div>
        <div className="flex justify-center pt-2"><PoweredBy testid="login-powered-by" /></div>
      </main>
    </div>
  );
}
